import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, Users, BedDouble, Bath, Wifi, Flame, Car, Shield, MapPin, ArrowRight } from 'lucide-react';
import { ReviewsSection } from '../components/ReviewsSection';
import { useLanguage } from '../context/LanguageContext';
import { useBusinessInfo } from '../context/BusinessContext';
import { getImageUrl } from '../utils/imageUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [cabins, setCabins] = useState([]);
  const [pointsOfInterest, setPointsOfInterest] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();
  const { getWhatsAppLink } = useBusinessInfo();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cabinsRes, poiRes, faqsRes] = await Promise.all([
          axios.get(`${API}/cabins`),
          axios.get(`${API}/points-of-interest`),
          axios.get(`${API}/faqs`)
        ]);
        setCabins(cabinsRes.data);
        setPointsOfInterest(poiRes.data.slice(0, 3));
        setFaqs(faqsRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const scrollToContent = () => {
    document.getElementById('cabins-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Get unique cabin models
  const cabinModels = [
    cabins.find(c => c.model_type === 'grande'),
    cabins.find(c => c.model_type === 'mediana')
  ].filter(Boolean);

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative h-screen flex items-end"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_67c604a8-26cc-4407-9dbe-fbd9eed391ea/artifacts/yldc3jjz_360_F_217967980_PsEp8B2g9tK9c6NkSnL2tmo9UG4uH2QW.jpg"
            alt="Mazamitla - Pueblo Mágico"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container-ribba pb-20 md:pb-32">
          <div className="max-w-3xl animate-fade-in-up">
            <span className="inline-block text-[#C6A87C] text-sm font-medium tracking-widest uppercase mb-4">
              {t('hero.subtitle')}
            </span>
            <h1 className="font-heading text-5xl md:text-7xl text-white font-bold leading-tight mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-white/80 text-lg md:text-xl mb-8 max-w-xl leading-relaxed">
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/reservar"
                data-testid="hero-reservar-btn"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                {t('hero.bookNow')}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/cabanas"
                data-testid="hero-ver-cabanas-btn"
                className="btn-secondary-light inline-flex items-center justify-center"
              >
                {t('hero.viewCabins')}
              </Link>
            </div>
          </div>

          {/* Scroll indicator */}
          <button
            onClick={scrollToContent}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors animate-bounce"
            aria-label="Scroll down"
          >
            <ChevronDown className="w-8 h-8" />
          </button>
        </div>
      </section>

      {/* Cabins Section */}
      <section
        id="cabins-section"
        data-testid="cabins-section"
        className="py-24 md:py-32 bg-[#F5F2EB]"
      >
        <div className="container-ribba">
          <div className="text-center mb-16">
            <span className="text-[#C6A87C] text-sm font-medium tracking-widest uppercase">
              {t('cabins.subtitle')}
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-[#1A1A1A] font-bold mt-3 mb-4">
              {t('cabins.title')}
            </h2>
            <p className="text-[#5A5A5A] max-w-2xl mx-auto">
              {t('cabins.description')}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {cabinModels.map((cabin) => (
                <div
                  key={cabin.id}
                  data-testid={`cabin-card-${cabin.model_type}`}
                  className="bg-white rounded-sm overflow-hidden card-shadow cabin-card"
                >
                  <div className="img-zoom aspect-cabin">
                    <img
                      src={getImageUrl(cabin.images[0])}
                      alt={cabin.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <span className="badge-gold mb-2 inline-block">
                          2 {t('cabins.available')}
                        </span>
                        <h3 className="font-heading text-2xl md:text-3xl text-[#1A1A1A] font-bold">
                          {language === 'es' 
                            ? `Cabaña ${cabin.model_type === 'grande' ? 'Grande' : 'Mediana'}`
                            : `${cabin.model_type === 'grande' ? 'Large' : 'Medium'} Cabin`
                          }
                        </h3>
                      </div>
                      <div className="text-right">
                        <span className="price-tag">${cabin.price_per_night.toLocaleString()}</span>
                        <span className="block text-[#5A5A5A] text-sm">{t('cabins.perNight')}</span>
                      </div>
                    </div>

                    <p className="text-[#5A5A5A] mb-6 line-clamp-2">
                      {language === 'es' 
                        ? cabin.description 
                        : t('cabins.cabinDescription')
                      }
                    </p>

                    <div className="flex flex-wrap gap-4 mb-6 text-[#5A5A5A] text-sm">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#2F3E30]" />
                        <span>{cabin.capacity} {t('cabins.guests')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BedDouble className="w-4 h-4 text-[#2F3E30]" />
                        <span>{cabin.bedrooms} {t('cabins.bedrooms')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Bath className="w-4 h-4 text-[#2F3E30]" />
                        <span>{cabin.bathrooms} {t('cabins.bathrooms')}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6">
                      {cabin.amenities.slice(0, 4).map((amenity, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-[#F5F2EB] text-[#5A5A5A] text-xs rounded-sm"
                        >
                          {amenity}
                        </span>
                      ))}
                    </div>

                    <div className="flex gap-3">
                      <Link
                        to={`/cabanas/${cabin.id}`}
                        className="flex-1 btn-secondary text-center text-sm py-3"
                      >
                        {t('cabins.viewDetails')}
                      </Link>
                      <Link
                        to={`/reservar?cabin=${cabin.id}`}
                        className="flex-1 btn-primary text-center text-sm py-3"
                      >
                        {t('cabins.book')}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/cabanas"
              data-testid="ver-todas-cabanas-btn"
              className="inline-flex items-center gap-2 text-[#2F3E30] font-medium hover:text-[#C6A87C] transition-colors"
            >
              {t('cabins.viewAll')}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section data-testid="features-section" className="py-24 bg-white">
        <div className="container-ribba">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Wifi, title: t('features.wifi'), desc: t('features.wifiDesc') },
              { icon: Flame, title: t('features.fireplace'), desc: t('features.fireplaceDesc') },
              { icon: Car, title: t('features.parking'), desc: t('features.parkingDesc') },
              { icon: Shield, title: t('features.security'), desc: t('features.securityDesc') }
            ].map((feature, idx) => (
              <div
                key={idx}
                className="text-center p-6"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-[#F5F2EB] rounded-full flex items-center justify-center">
                  <feature.icon className="w-7 h-7 text-[#2F3E30]" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#5A5A5A] text-sm">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Points of Interest Section */}
      <section
        data-testid="poi-section"
        className="py-24 md:py-32 bg-[#F5F2EB]"
      >
        <div className="container-ribba">
          <div className="text-center mb-16">
            <span className="text-[#C6A87C] text-sm font-medium tracking-widest uppercase">
              {t('mazamitla.subtitle')}
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-[#1A1A1A] font-bold mt-3 mb-4">
              {t('mazamitla.title')}
            </h2>
            <p className="text-[#5A5A5A] max-w-2xl mx-auto">
              {t('mazamitla.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pointsOfInterest.map((poi) => (
              <div
                key={poi.id}
                data-testid={`poi-card-${poi.id}`}
                className="group relative overflow-hidden rounded-sm aspect-[4/5]"
              >
                <img
                  src={poi.image}
                  alt={poi.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-[#C6A87C]" />
                    <span className="text-[#C6A87C] text-xs uppercase tracking-wider">
                      {poi.category}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl text-white font-semibold mb-2">
                    {poi.name}
                  </h3>
                  <p className="text-white/70 text-sm line-clamp-2">
                    {poi.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/mazamitla"
              data-testid="ver-mazamitla-btn"
              className="inline-flex items-center gap-2 text-[#2F3E30] font-medium hover:text-[#C6A87C] transition-colors"
            >
              {t('mazamitla.exploreMore')}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection showForm={true} />

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container-ribba">
          <div className="text-center mb-12">
            <span className="text-[#C6A87C] text-sm font-medium tracking-widest uppercase">
              {language === 'es' ? 'Resuelve tus dudas' : 'Get your answers'}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1A1A1A] mt-3">
              {language === 'es' ? 'Preguntas Frecuentes' : 'Frequently Asked Questions'}
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.length > 0 ? (
              faqs.map((faq) => (
                <div key={faq.id} className="p-6 bg-[#F5F2EB] rounded-sm">
                  <h3 className="font-semibold text-[#1A1A1A] mb-2">
                    {language === 'es' ? faq.question_es : (faq.question_en || faq.question_es)}
                  </h3>
                  <p className="text-[#5A5A5A] text-sm">
                    {language === 'es' ? faq.answer_es : (faq.answer_en || faq.answer_es)}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-center text-[#5A5A5A]">
                {language === 'es' ? 'Cargando...' : 'Loading...'}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        data-testid="cta-section"
        className="relative py-24 md:py-32"
      >
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_mazamitla-lodge/artifacts/3jrtrp3h_Modelo%201-2.png"
            alt="Interior cabaña"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#2F3E30]/85" />
        </div>

        <div className="relative z-10 container-ribba text-center">
          <h2 className="font-heading text-4xl md:text-5xl text-white font-bold mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
            {t('cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/reservar"
              data-testid="cta-reservar-btn"
              className="btn-primary bg-[#C6A87C] text-[#1A1A1A] hover:bg-white inline-flex items-center justify-center gap-2"
            >
              {t('cta.bookNow')}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-btn"
              className="btn-secondary-light inline-flex items-center justify-center"
            >
              {t('cta.whatsapp')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
